import type { PumpkinKind, PumpkinShape } from '../types';
import { SCULPT_W, SCULPT_H } from '../types';
import { V3, v3, rng, noise3, smoothstep, clamp } from '../engine/math';
import { Builder, MAT } from '../engine/builder';

// A pumpkin is a surface of revolution bent out of true: s runs 0 (top) to 1 (bottom), phi round the middle. Front
// is +z (phi = pi/2), which is where the stencil goes.
//
// The form follows real squash: broad rounded lobes of unequal width, parted by narrow V creases that deepen toward
// the stem, where the top sinks into a well with a raised rim; a base flattened to sit on; a faint twist and lean.
// Ornamental gourds (pear, bottle, swan, crookneck) take their outline from a hand-set profile curve instead.
//
// Optional shape fields beyond the original ones (saved scenes lack them, every one has a default):
//   waist    0..1  hourglass pinch at the equator (shishigatani)
//   fingers  0..1  finger-like projections round the blossom end, one per lobe (crown of thorns)
//   flat     0..1  how flat the base is, default 0.6
//   well     0..1  depth and width of the pit round the stem, default follows `dimple`
//   pattern        skin colours and markings, all colours LINEAR RGB:
//     base2    second skin colour (stripes, bicolor bottom, mottle, flecks)
//     groove   colour in the creases; absent = a darker, greener skin colour
//     speckle  0..1 small flecks of base2 (swan gourd cream flecks)
//     marble   0..1 medium marbling of base2 (speckled hound's blue-green)
//     stripes  0..1 narrow bands of base2 running stem to blossom, `stripeN` of them
//     bicolor  0..1 where the skin turns from skin (above) to base2 (below), as a share of the height; 0 = off
//     mottle   0..1 low-frequency blotches of base2

export const KINDS: { kind: PumpkinKind; label: string }[] = [
  { kind: 'field', label: 'Field' }, { kind: 'tall', label: 'Tall' }, { kind: 'cinderella', label: 'Cinderella' },
  { kind: 'ghost', label: 'Ghost white' }, { kind: 'jarrahdale', label: 'Jarrahdale blue' }, { kind: 'warty', label: 'Warty goblin' },
  { kind: 'mini', label: 'Munchkin' }, { kind: 'bottle', label: 'Bottle gourd' }, { kind: 'crookneck', label: 'Crookneck' },
  { kind: 'turban', label: 'Turban' }, { kind: 'musquee', label: 'Musquee de Provence' }, { kind: 'shishigatani', label: 'Shishigatani' },
  { kind: 'pear', label: 'Pear gourd' }, { kind: 'thorns', label: 'Crown of thorns' }, { kind: 'swan', label: 'Swan gourd' },
  { kind: 'bicolor', label: 'Bicolor gourd' }, { kind: 'hound', label: 'Speckled hound' },
];

const SKINS: Record<PumpkinKind, V3> = {
  field: [0.72, 0.2, 0.02], tall: [0.66, 0.17, 0.015], cinderella: [0.5, 0.07, 0.012], ghost: [0.82, 0.78, 0.66],
  jarrahdale: [0.21, 0.33, 0.32], warty: [0.6, 0.12, 0.015], mini: [0.75, 0.3, 0.02], bottle: [0.32, 0.4, 0.15],
  crookneck: [0.78, 0.5, 0.04], turban: [0.6, 0.14, 0.02], musquee: [0.64, 0.32, 0.1], shishigatani: [0.62, 0.22, 0.03],
  pear: [0.62, 0.52, 0.16], thorns: [0.7, 0.45, 0.04], swan: [0.07, 0.17, 0.05], bicolor: [0.7, 0.5, 0.03], hound: [0.7, 0.2, 0.02],
};

export function kindPreset(kind: PumpkinKind, seed = (Math.random() * 1e9) | 0): PumpkinShape {
  const r = rng(seed), j = (a: number, b: number) => a + (b - a) * r();
  const base: PumpkinShape = {
    kind, seed, ribs: Math.round(j(9, 12)), ribDepth: j(0.45, 0.7), ribSharp: j(0.35, 0.6), squash: j(0.78, 0.92),
    size: j(0.17, 0.21), lean: j(-0.25, 0.25), lopsided: j(0.3, 0.55), warts: 0, dimple: j(0.5, 0.75), neck: 0,
    stem: { length: j(0.3, 0.6), curl: j(-0.6, 0.6), thick: j(0.5, 0.8), tilt: j(-0.3, 0.3) },
    skin: [...SKINS[kind]] as V3, ribTint: j(0.3, 0.6),
  };
  const P = (p: NonNullable<PumpkinShape['pattern']>) => { base.pattern = p; };
  switch (kind) {
    case 'field': P({ groove: [0.34, 0.1, 0.012], base2: [0.62, 0.28, 0.03], mottle: 0.25 }); break;
    case 'tall': Object.assign(base, { squash: j(1.1, 1.3), ribs: Math.round(j(10, 13)), ribDepth: j(0.3, 0.5), size: j(0.15, 0.18), dimple: j(0.35, 0.5) });
      P({ groove: [0.3, 0.09, 0.01], base2: [0.55, 0.2, 0.02], mottle: 0.2 }); break;
    case 'cinderella': Object.assign(base, { squash: j(0.5, 0.6), ribs: Math.round(j(10, 13)), ribDepth: j(0.8, 1), ribSharp: j(0.6, 0.8), size: j(0.21, 0.25), dimple: j(0.8, 0.95), well: 0.9, flat: 0.8 });
      P({ groove: [0.2, 0.03, 0.008], base2: [0.6, 0.17, 0.02], mottle: 0.35 }); break;
    case 'ghost': Object.assign(base, { squash: j(0.72, 0.85), ribTint: j(0.15, 0.3) });
      P({ groove: [0.42, 0.48, 0.3], base2: [0.52, 0.58, 0.42], mottle: 0.3 }); break;
    case 'jarrahdale': Object.assign(base, { squash: j(0.58, 0.7), ribs: Math.round(j(10, 13)), ribDepth: j(0.75, 0.95), ribSharp: j(0.55, 0.75), size: j(0.2, 0.24), well: 0.8, flat: 0.75 });
      P({ groove: [0.09, 0.15, 0.15], base2: [0.4, 0.46, 0.34], mottle: 0.45 }); break;
    case 'warty': Object.assign(base, { warts: j(0.8, 1), lopsided: j(0.5, 0.8), ribTint: j(0.5, 0.8), ribs: Math.round(j(8, 11)), ribDepth: j(0.3, 0.5), squash: j(0.85, 0.98) });
      P({ groove: [0.3, 0.08, 0.01], base2: [0.55, 0.3, 0.05], mottle: 0.3 }); break;
    case 'mini': Object.assign(base, { size: j(0.06, 0.08), squash: j(0.55, 0.7), ribs: Math.round(j(8, 10)), ribDepth: j(0.7, 0.9), stem: { ...base.stem, length: j(0.7, 1) }, flat: 0.8 });
      P({ groove: [0.4, 0.15, 0.01], base2: [0.85, 0.5, 0.05], mottle: 0.15 }); break;
    case 'bottle': Object.assign(base, { neck: j(0.6, 0.9), ribs: 6, ribDepth: j(0.04, 0.08), size: j(0.12, 0.15), squash: 1, dimple: 0, ribTint: 0.1, stem: { ...base.stem, length: 0.1, thick: 0.5 } });
      P({ base2: [0.55, 0.55, 0.28], mottle: 0.4 }); break;
    case 'crookneck': Object.assign(base, { neck: j(0.6, 0.85), ribs: 7, ribDepth: j(0.05, 0.1), size: j(0.1, 0.13), squash: 1, warts: j(0.3, 0.6), lean: (r() < 0.5 ? -1 : 1) * j(0.6, 1), dimple: 0, ribTint: 0.1, stem: { ...base.stem, length: 0.1, thick: 0.4 } });
      P({ base2: [0.85, 0.65, 0.1], mottle: 0.2, groove: [0.55, 0.3, 0.02] }); break;
    case 'turban': Object.assign(base, { squash: j(0.65, 0.75), ribs: Math.round(j(9, 11)), ribDepth: j(0.4, 0.6), size: j(0.17, 0.2) });
      P({ base2: [0.1, 0.25, 0.1], mottle: 0.3 }); break;
    case 'musquee': Object.assign(base, { squash: j(0.55, 0.66), ribs: Math.round(j(9, 11)), ribDepth: j(0.75, 0.95), ribSharp: j(0.6, 0.8), size: j(0.2, 0.24), well: 0.85, flat: 0.8, dimple: 0.85, stem: { length: j(0.2, 0.4), curl: j(-0.3, 0.3), thick: j(0.8, 1), tilt: j(-0.2, 0.2) } });
      P({ groove: [0.1, 0.17, 0.05], base2: [0.16, 0.24, 0.07], mottle: 0.35 }); break;
    case 'shishigatani': Object.assign(base, { squash: j(0.95, 1.1), waist: j(0.45, 0.65), ribs: Math.round(j(8, 10)), ribDepth: j(0.5, 0.7), warts: j(0.3, 0.5), size: j(0.15, 0.17), lopsided: 0.5 });
      P({ groove: [0.15, 0.16, 0.03], base2: [0.15, 0.25, 0.05], mottle: 0.3 }); break;
    case 'pear': Object.assign(base, { ribs: 7, ribDepth: j(0.06, 0.12), ribSharp: 0.2, size: j(0.11, 0.14), squash: 1, dimple: 0, neck: 0.3, lopsided: 0.3, ribTint: 0.15, stem: { length: 0.2, curl: j(-0.4, 0.4), thick: 0.6, tilt: j(-0.2, 0.2) } });
      P({ base2: [0.05, 0.19, 0.035], stripes: 0.9, stripeN: 16, mottle: 0.15 }); break;
    case 'thorns': Object.assign(base, { squash: j(0.62, 0.78), ribs: Math.round(j(6, 8)), ribDepth: j(0.35, 0.55), fingers: j(0.75, 1), size: j(0.14, 0.17), flat: 0.2, dimple: 0.5, stem: { ...base.stem, length: 0.2 } });
      P({ base2: [0.07, 0.2, 0.035], bicolor: 0.6, mottle: 0.2 }); break;
    case 'swan': Object.assign(base, { neck: 1, ribs: 6, ribDepth: 0.04, size: j(0.09, 0.11), squash: 1, dimple: 0, lopsided: 0.3, ribTint: 0.1, lean: 1, stem: { length: 0.05, curl: 0, thick: 0.3, tilt: 0 } });
      P({ base2: [0.6, 0.55, 0.32], speckle: 0.85 }); break;
    case 'bicolor': Object.assign(base, { ribs: 6, ribDepth: j(0.04, 0.08), size: j(0.1, 0.12), squash: 1, dimple: 0, neck: 0.1, ribTint: 0.1, stem: { length: 0.25, curl: j(-0.5, 0.5), thick: 0.5, tilt: j(-0.2, 0.2) } });
      P({ base2: [0.05, 0.22, 0.035], bicolor: 0.46, mottle: 0.15 }); break;
    case 'hound': Object.assign(base, { squash: j(0.84, 1), warts: j(0.15, 0.3), ribs: Math.round(j(10, 12)), ribDepth: j(0.4, 0.6), size: j(0.17, 0.2) });
      P({ groove: [0.3, 0.1, 0.02], base2: [0.06, 0.3, 0.26], marble: 0.9, speckle: 0.3, mottle: 0.1 }); break;
  }
  return base;
}


export interface Variety { key: string; label: string; group: string; make(seed: number): PumpkinShape }

const V = (key: string, label: string, group: string, kind: PumpkinKind, size?: [number, number]): Variety => ({
  key, label, group,
  make(seed) {
    const sh = kindPreset(kind, seed);
    if (size) { const r = rng(seed ^ 0x51ce); sh.size = size[0] + (size[1] - size[0]) * r(); }
    sh.variety = key;
    return sh;
  },
});

/** the pick-one-to-carve shelf: sizes sit in a range where three stand together on a stage */
export const VARIETIES: Variety[] = [
  V('field', 'Orange field pumpkin', 'orange', 'field', [0.17, 0.2]),
  V('ghost', 'Ghost white', 'white', 'ghost', [0.17, 0.2]),
  V('jarrahdale', 'Jarrahdale blue', 'blue', 'jarrahdale', [0.18, 0.21]),
  V('warty', 'Red warty thing', 'red', 'warty', [0.16, 0.19]),
  V('hound', 'Speckled hound', 'orange', 'hound', [0.16, 0.19]),
  V('cinderella', 'Cinderella', 'red', 'cinderella', [0.19, 0.22]),
  V('musquee', 'Musquee de Provence', 'tan', 'musquee', [0.18, 0.21]),
  V('shishigatani', 'Shishigatani', 'orange', 'shishigatani', [0.14, 0.16]),
  V('tall', 'Long pie', 'orange', 'tall', [0.14, 0.16]),
  V('thorns', 'Crown of thorns', 'green', 'thorns', [0.13, 0.15]),
  V('munchkin', 'Munchkin', 'orange', 'mini', [0.1, 0.12]),
  V('pear', 'Pear gourd', 'green', 'pear', [0.12, 0.14]),
  V('bicolor', 'Bicolor gourd', 'green', 'bicolor', [0.11, 0.13]),
  V('swan', 'Speckled swan gourd', 'green', 'swan'),
  V('crookneck', 'Crookneck', 'yellow', 'crookneck'),
  V('bottle', 'Bottle gourd', 'green', 'bottle'),
];

/** the ones worth carving; the swan, crookneck and bottle are for looking at */
const CARVABLE = new Set(['field', 'ghost', 'jarrahdale', 'warty', 'hound', 'cinderella', 'musquee', 'shishigatani', 'tall', 'thorns', 'munchkin', 'pear', 'bicolor']);

/** n shapes of different varieties, different colour groups where it can, deterministic by seed */
export function randomVarieties(n: number, seed = (Math.random() * 1e9) | 0): PumpkinShape[] {
  const r = rng(seed);
  const pool = VARIETIES.filter(v => CARVABLE.has(v.key));
  for (let i = pool.length - 1; i > 0; i--) { const k = Math.floor(r() * (i + 1)); [pool[i], pool[k]] = [pool[k], pool[i]]; }
  const out: Variety[] = [], groups = new Set<string>();
  for (const v of pool) { if (out.length < n && !groups.has(v.group)) { out.push(v); groups.add(v.group); } }
  for (const v of pool) { if (out.length < n && !out.includes(v)) out.push(v); }
  return out.slice(0, n).map((v, i) => v.make((seed + i * 7919) | 0));
}

/** the sculpt grid, decoded; offsets as a share of the radius */
export function decodeSculpt(s?: string): Float32Array {
  const g = new Float32Array(SCULPT_W * SCULPT_H);
  if (!s) return g;
  let bin = '';
  try { bin = atob(s); } catch { return g; }
  for (let i = 0; i < g.length && i < bin.length; i++) g[i] = ((bin.charCodeAt(i) << 24) >> 24) / 400;
  return g;
}

export function encodeSculpt(g: Float32Array): string | undefined {
  let any = false, out = '';
  for (let i = 0; i < g.length; i++) {
    const q = clamp(Math.round(g[i] * 400), -127, 127);
    if (q) any = true;
    out += String.fromCharCode(q & 255);
  }
  return any ? btoa(out) : undefined;
}

function sampleGrid(g: Float32Array, u: number, s: number): number {
  const x = (((u % 1) + 1) % 1) * SCULPT_W - 0.5, y = clamp(s * (SCULPT_H - 1), 0, SCULPT_H - 1);
  const x0 = Math.floor(x), y0 = Math.floor(y), fx = x - x0, fy = y - y0;
  const X = (i: number) => ((i % SCULPT_W) + SCULPT_W) % SCULPT_W, Y = (i: number) => Math.min(SCULPT_H - 1, i);
  const at = (i: number, k: number) => g[Y(k) * SCULPT_W + X(i)];
  return (at(x0, y0) * (1 - fx) + at(x0 + 1, y0) * fx) * (1 - fy) + (at(x0, y0 + 1) * (1 - fx) + at(x0 + 1, y0 + 1) * fx) * fy;
}

interface Wart { s: number; phi: number; r: number; h: number }

/** hand-set outlines for the gourds, (radius, height) in bulb radii from the top pole down; smooth through the poles */
const CURVES: Partial<Record<PumpkinKind, [number, number][]>> = {
  bottle: [[0, 1.9], [0.16, 1.88], [0.25, 1.78], [0.28, 1.55], [0.3, 1.25], [0.38, 1.0], [0.58, 0.8], [0.84, 0.55], [0.99, 0.15], [1, -0.25], [0.88, -0.6], [0.55, -0.86], [0.28, -0.95], [0, -0.97]],
  pear: [[0, 1.7], [0.16, 1.68], [0.27, 1.52], [0.35, 1.3], [0.48, 1.02], [0.7, 0.66], [0.92, 0.22], [1, -0.2], [0.92, -0.6], [0.62, -0.87], [0.3, -0.95], [0, -0.97]],
  bicolor: [[0, 1.45], [0.15, 1.43], [0.28, 1.3], [0.4, 1.08], [0.64, 0.82], [0.9, 0.46], [1, 0], [0.95, -0.42], [0.7, -0.78], [0.36, -0.94], [0, -0.97]],
  crookneck: [[0, 2.3], [0.17, 2.28], [0.27, 2.14], [0.33, 1.8], [0.42, 1.36], [0.58, 0.96], [0.82, 0.55], [0.98, 0.1], [1, -0.3], [0.85, -0.7], [0.45, -0.93], [0, -0.97]],
  swan: [[0, 2.9], [0.12, 2.88], [0.17, 2.72], [0.19, 2.25], [0.22, 1.7], [0.34, 1.18], [0.62, 0.8], [0.88, 0.4], [1, -0.05], [0.95, -0.5], [0.65, -0.85], [0.3, -0.95], [0, -0.97]],
};
/** bend of the upper part, per radius, and the height it starts at */
const BEND: Partial<Record<PumpkinKind, number>> = { crookneck: 0.5, swan: 0.95, bottle: 0.12 };

/** a Catmull-Rom curve through the points, mirrored at the poles, walked by arc length: s in 0..1 gives (rho, y) */
function curveProfile(pts: [number, number][], neckScale: number): (s: number) => [number, number] {
  const P = pts.map(([x, y]) => [x, y > 0.8 ? 0.8 + (y - 0.8) * neckScale : y] as [number, number]);
  const ext = [[-P[1][0], P[1][1]], ...P, [-P[P.length - 2][0], P[P.length - 2][1]]] as [number, number][];
  const N = 32, tab: [number, number][] = [];
  for (let i = 1; i < ext.length - 2; i++) {
    const [a, b, c, d] = [ext[i - 1], ext[i], ext[i + 1], ext[i + 2]];
    for (let k = 0; k < N; k++) {
      const t = k / N, t2 = t * t, t3 = t2 * t;
      const f = (q: number) => 0.5 * ((2 * b[q]) + (-a[q] + c[q]) * t + (2 * a[q] - 5 * b[q] + 4 * c[q] - d[q]) * t2 + (-a[q] + 3 * b[q] - 3 * c[q] + d[q]) * t3);
      tab.push([Math.max(0, f(0)), f(1)]);
    }
  }
  tab.push(P[P.length - 1]);
  const cum = [0];
  for (let i = 1; i < tab.length; i++) cum.push(cum[i - 1] + Math.hypot(tab[i][0] - tab[i - 1][0], tab[i][1] - tab[i - 1][1]));
  const total = cum[cum.length - 1];
  return s => {
    const L = clamp(s, 0, 1) * total;
    let lo = 0, hi = cum.length - 1;
    while (hi - lo > 1) { const m = (lo + hi) >> 1; if (cum[m] <= L) lo = m; else hi = m; }
    const f = (L - cum[lo]) / Math.max(1e-9, cum[hi] - cum[lo]);
    return [tab[lo][0] + (tab[hi][0] - tab[lo][0]) * f, tab[lo][1] + (tab[hi][1] - tab[lo][1]) * f];
  };
}

/** a pumpkin's surface as a function, made once for a shape and called per vertex */
export function surface(shape: PumpkinShape, sculpt: Float32Array) {
  const r = rng(shape.seed ^ 0x5eed);
  const n = Math.max(3, Math.round(shape.ribs));
  const lop = shape.lopsided;
  // lobes of unequal width, bulge and height; creases sit on the cumulative edges
  const wid: number[] = [], ribAmp: number[] = [], ribH: number[] = [];
  let tot = 0;
  for (let k = 0; k < n; k++) { const w = 1 + (r() - 0.5) * 1.1 * lop; wid.push(w); tot += w; ribAmp.push(1 + (r() - 0.5) * 0.16 * lop); ribH.push(1 + (r() - 0.5) * 0.1 * lop); }
  const cum: number[] = [0];
  for (let k = 0; k < n; k++) cum.push(cum[k] + wid[k] / tot);
  // turn the ribs so a lobe, not a crease, faces front (+z, u = 0.25)
  const lobeAtFront = cum[Math.floor(0.25 * n)] + (cum[Math.floor(0.25 * n) + 1] - cum[Math.floor(0.25 * n)]) / 2;
  const ofs = 0.25 - lobeAtFront, wobPhase = r() * 6.283, twist = (r() - 0.5) * 0.5 / n;
  const warts: Wart[] = [], bands: Wart[][] = Array.from({ length: 16 }, () => []);
  const nw = Math.round(shape.warts * 240);
  for (let k = 0; k < nw; k++) {
    const s = 0.1 + 0.72 * Math.pow(r(), 1.2), big = r() < 0.2;
    const w = { s, phi: r() * Math.PI * 2, r: (big ? 0.035 : 0.016) + 0.03 * r() * r(), h: 0.01 + 0.04 * r() * shape.warts };
    warts.push(w);
    bands[Math.min(15, Math.floor(s * 16))].push(w);
  }
  const kind = shape.kind;
  const curve = CURVES[kind] ? curveProfile(CURVES[kind]!, 0.65 + 0.6 * shape.neck) : null;
  const bendSign = shape.lean < 0 ? -1 : 1, bendK = (BEND[kind] ?? 0) * (kind === 'crookneck' ? 0.5 + 0.5 * Math.abs(shape.lean) : 1) * bendSign;
  const bendY0 = 0.7;
  const flat = shape.flat ?? 0.6, well = shape.well ?? shape.dimple;
  const waist = shape.waist ?? 0, fingers = shape.fingers ?? 0;
  const turban = kind === 'turban';
  const pTop = 0.88, pBot = 0.9 - 0.25 * flat, yBot = 1 - 0.45 * flat;
  const D = 0.04 + shape.ribDepth * 0.2, kk = 1.8 + 1.4 * shape.ribSharp, pe = 0.62 - 0.3 * shape.ribSharp;
  let topDir: V3 = [0, 1, 0];
  if (bendK) {
    const yt = curve!(0)[1], th = bendK * Math.max(0, yt - bendY0);
    topDir = [Math.sin(th), Math.cos(th), 0];
  }

  /** position in metres (pumpkin space, centre of the bulb at the origin) and the groove and wart weights there */
  const f = (s: number, phi: number): { p: V3; q: V3; groove: number; wart: number } => {
    let rho: number, y: number;
    if (curve) [rho, y] = curve(s);
    else {
      const a = Math.PI * s, sa = Math.sin(a), ca = Math.cos(a);
      rho = ca >= 0 ? Math.pow(sa, pTop) : Math.pow(sa, pBot);
      y = ca >= 0 ? Math.pow(ca, 0.92) : -Math.pow(-ca, yBot);
      if (waist) rho *= 1 - waist * 0.38 * Math.exp(-(((s - 0.5) / 0.15) ** 2));
      if (turban) rho *= 1 - 0.16 * Math.exp(-(((s - 0.34) / 0.05) ** 2)) + 0.05 * (s < 0.34 ? 1 : 0) * Math.sin(Math.PI * s / 0.34);
    }
    // which lobe, and where across it
    let u = phi / (Math.PI * 2) - ofs + twist * Math.sin(s * 5 + wobPhase) + 0.03 / n * Math.sin(s * 11 + wobPhase * 2);
    u -= Math.floor(u);
    let k = 0; while (k < n - 1 && cum[k + 1] <= u) k++;
    const t = (u - cum[k]) / (cum[k + 1] - cum[k]);
    const c = Math.abs(2 * t - 1);
    const bulge = Math.pow(Math.max(0, 1 - Math.pow(c, kk)), pe);
    // neighbouring lobes' amplitudes blend across each lobe so the creases never tear
    const x = k + t - 0.5, i0 = Math.floor(x), fx = x - i0, sm = fx * fx * (3 - 2 * fx);
    const m = (i: number) => ((i % n) + n) % n;
    const amp = ribAmp[m(i0)] * (1 - sm) + ribAmp[m(i0 + 1)] * sm, hamp = ribH[m(i0)] * (1 - sm) + ribH[m(i0 + 1)] * sm;
    // creases deepen toward the stem and fade into the poles
    const depthS = smoothstep(0.0, 0.4, rho) * (0.75 + 0.65 * smoothstep(0.95, 0.4, rho) * (y > 0 ? 1 : 0.55));
    const crease = (1 - bulge) * depthS;
    rho *= amp * (1 - D * crease);
    rho *= 1 + 0.0035 * Math.sin(phi * n * 6 + y * 4);
    // the well round the stem with a raised rim, and a small scar at the blossom end
    if (!curve) {
      const wr = Math.min(1, rho);
      y += y > 0 ? -0.3 * well * Math.exp(-((wr / (0.28 + 0.1 * well)) ** 2)) : 0.06 * Math.exp(-((wr / 0.3) ** 2));
      if (y > 0) y += 0.05 * well * Math.exp(-(((wr - 0.5) / 0.14) ** 2));
    }
    y *= shape.squash * hamp;
    // crown of thorns: a horn from each lobe round the blossom end, a cone with hollow sides so it comes to a point,
    // tipped down, on a low flare of the lobe
    if (fingers) {
      const w = smoothstep(0.5, 0.68, s) * (1 - smoothstep(0.74, 0.86, s)), pk = Math.pow(1 - c, 1.4);
      const d = Math.hypot(c / 0.5, (s - 0.67) / 0.11), horn = d < 1 ? Math.pow(1 - d, 1.5) : 0;
      rho *= 1 + fingers * (0.16 * pk * w + 0.52 * horn); y -= fingers * (0.05 * pk * w + 0.16 * horn);
    }
    // lopsided: low-frequency noise on the whole form
    const dx = Math.cos(phi) * rho, dz = Math.sin(phi) * rho;
    const lump = (noise3(dx * 1.3 + 3, y * 1.3, dz * 1.3, shape.seed) - 0.5) * 2;
    rho *= 1 + lop * 0.12 * lump;
    y *= 1 + lop * 0.06 * lump;
    // warts
    let wart = 0;
    const b = Math.min(15, Math.floor(s * 16)), sr = Math.max(rho, 0.15);
    for (let q = Math.max(0, b - 1); q <= Math.min(15, b + 1); q++) for (const w of bands[q]) {
      let dp = Math.abs(phi - w.phi); if (dp > Math.PI) dp = Math.PI * 2 - dp;
      const d2 = ((s - w.s) * Math.PI) ** 2 + (dp * sr) ** 2;
      if (d2 < w.r * w.r * 4) wart += w.h * Math.exp(-d2 / (w.r * w.r));
    }
    const sc = sampleGrid(sculpt, phi / (Math.PI * 2), s);
    const grow = 1 + wart + sc;
    let px = Math.cos(phi) * rho * grow, pz = Math.sin(phi) * rho * grow, py = y * grow;
    // where the carving is looked up: the neck before it bends, so a neck curling back over the body still runs on up
    // the sheet, and a lid cut takes all of it
    const q: V3 = [px * shape.size, py * shape.size, pz * shape.size];
    if (bendK) {
      // gourds: the neck bent round a circle, the cross-section turning with it
      const h = Math.max(0, py - bendY0), th = bendK * h;
      const cx = (1 - Math.cos(th)) / bendK, cy = Math.sin(th) / bendK;
      const ox = px; px = cx + ox * Math.cos(th); py = (py - h) + cy - ox * Math.sin(th);
    } else {
      // lean: the top pushed over
      px += shape.lean * 0.16 * Math.max(0, py);
    }
    const p: V3 = [px * shape.size, py * shape.size, pz * shape.size];
    return { p, q: bendK ? q : p, groove: Math.min(1, crease * 1.2), wart: Math.min(1, wart * 12) };
  };
  return Object.assign(f, { topDir });
}

export interface PumpkinGeometry {
  /** pos 3, normal 3, uv 2 (phi/2pi, s), groove, wart, sheet point 3 (where the carving is looked up) */
  verts: Float32Array;
  index: Uint32Array;
  /** the stem as prop geometry (pos, normal, colour, material) */
  stem: { v: Float32Array; i: Uint32Array };
  /** lowest y, for setting it down; top of the stem's base */
  minY: number;
  top: V3;
  /** the mean radius of the bulb, for the light's exit through the shell */
  radius: number;
  cols: number;
  rows: number;
}

/** floats per vertex in a pumpkin's mesh */
export const STRIDE = 13;

export function buildPumpkin(shape: PumpkinShape, sculpt: Float32Array, detail: 'low' | 'high'): PumpkinGeometry {
  if (shape.flaws) shape = { ...shape, lopsided: Math.min(1, shape.lopsided + shape.flaws * 0.6), warts: Math.min(1, shape.warts + shape.flaws * 0.5), ribDepth: Math.min(1, shape.ribDepth + shape.flaws * 0.1) };
  const cols = detail === 'high' ? 192 : 96, rows = detail === 'high' ? 96 : 48;
  const f = surface(shape, sculpt);
  const nv = (cols + 1) * (rows + 1), verts = new Float32Array(nv * STRIDE);
  const pos: V3[] = [], qs: V3[] = [];
  let minY = Infinity, sumR = 0;
  for (let k = 0; k <= rows; k++) {
    // rows bunched toward the poles a little, where the well needs them
    const t = k / rows, s = t - Math.sin(2 * Math.PI * t) * 0.06;
    for (let j = 0; j <= cols; j++) {
      const phi = (j / cols) * Math.PI * 2, o = f(s, phi), i = k * (cols + 1) + j;
      pos.push(o.p); qs.push(o.q);
      verts.set([o.p[0], o.p[1], o.p[2], 0, 0, 0, j / cols, s, o.groove, o.wart, o.q[0], o.q[1], o.q[2]], i * STRIDE);
      minY = Math.min(minY, o.p[1]);
      if (k === rows >> 1) sumR += Math.hypot(o.p[0], o.p[2]);
    }
  }
  const index = new Uint32Array(rows * cols * 6);
  let q = 0;
  for (let k = 0; k < rows; k++) for (let j = 0; j < cols; j++) {
    const a = k * (cols + 1) + j, b = a + cols + 1;
    index.set([a, a + 1, b, a + 1, b + 1, b], q); q += 6;
  }
  const nrm = normalsOf(pos, index, rows, cols);
  for (let i = 0; i < nv; i++) verts.set(nrm.subarray(i * 3, i * 3 + 3), i * STRIDE + 3);
  const top = pos[0];
  return { verts, index, stem: buildStem(shape, top, f.topDir), minY, top, radius: sumR / (cols + 1), cols, rows };
}

/** unit normals from the faces round each vertex, the seam's two columns shared and each pole one point */
function normalsOf(pos: V3[], index: Uint32Array, rows: number, cols: number): Float32Array {
  const nrm = new Float32Array(pos.length * 3);
  for (let t = 0; t < index.length; t += 3) {
    const A = pos[index[t]], B = pos[index[t + 1]], C = pos[index[t + 2]];
    const fn = v3.cross(v3.sub(B, A), v3.sub(C, A));
    for (const ix of [index[t], index[t + 1], index[t + 2]]) { nrm[ix * 3] += fn[0]; nrm[ix * 3 + 1] += fn[1]; nrm[ix * 3 + 2] += fn[2]; }
  }
  for (let k = 0; k <= rows; k++) {
    const a = k * (cols + 1), b = a + cols;
    for (let c = 0; c < 3; c++) { const sum = nrm[a * 3 + c] + nrm[b * 3 + c]; nrm[a * 3 + c] = nrm[b * 3 + c] = sum; }
  }
  for (const k of [0, rows]) {
    let sx = 0, sy = 0, sz = 0;
    for (let j = 0; j <= cols; j++) { const i = (k * (cols + 1) + j) * 3; sx += nrm[i]; sy += nrm[i + 1]; sz += nrm[i + 2]; }
    for (let j = 0; j <= cols; j++) { const i = (k * (cols + 1) + j) * 3; nrm[i] = sx; nrm[i + 1] = sy; nrm[i + 2] = sz; }
  }
  for (let i = 0; i < pos.length; i++) nrm.set(v3.norm([nrm[i * 3], nrm[i * 3 + 1], nrm[i * 3 + 2]]), i * 3);
  return nrm;
}

function buildStem(shape: PumpkinShape, top: V3, topDir: V3 = [0, 1, 0]) {
  const b = new Builder(), r = rng(shape.seed ^ 0x57e3);
  const st = shape.stem, size = shape.size;
  const small = shape.kind === 'mini' ? 1.6 : 1;
  const wild = st.wild ?? 0;
  const len = size * (0.18 + 0.75 * st.length) * small * (1 + 3 * wild);
  const rad = size * (0.04 + 0.06 * st.thick) * (shape.kind === 'mini' ? 1.5 : 1);
  const spine: V3[] = [], radius: number[] = [];
  const dir0: V3 = v3.norm([topDir[0] + Math.sin(st.tilt * 0.6), topDir[1], topDir[2] + Math.cos(st.tilt * 0.6) * 0.2]);
  let dir = dir0;
  // starts a little below the pole, inside the well, so the flare seats into the skin
  let p: V3 = v3.add(top, v3.scale(topDir, -rad * 0.9));
  const steps = 14 + Math.round(wild * 30), yaw = r() * Math.PI * 2;
  // a frame round the growing direction, for the corkscrew
  const ax: V3 = v3.norm(v3.cross(dir0, Math.abs(dir0[1]) > 0.9 ? [1, 0, 0] : [0, 1, 0])), ay: V3 = v3.cross(dir0, ax);
  for (let k = 0; k <= steps; k++) {
    const t = k / steps;
    spine.push(p);
    // a flared foot where it meets the skin, a taper, and a slight swell at the cut end
    radius.push(rad * (1 + 1.4 * Math.exp(-t * 11)) * (1 - (0.32 + 0.4 * wild) * t) * (1 + 0.12 * smoothstep(0.85, 1, t)));
    if (wild > 0) {
      // bends further and further off the axis while the heading winds round it: a tendril
      const bend = wild * (0.5 + 1.7 * t), head = yaw + wild * t * 17;
      const side = v3.add(v3.scale(ax, Math.cos(head)), v3.scale(ay, Math.sin(head)));
      dir = v3.norm(v3.add(v3.scale(dir0, Math.cos(bend)), v3.scale(side, Math.sin(bend))));
    } else {
      const turn = st.curl * 0.3 * t;
      dir = v3.norm(v3.add(dir, [Math.cos(yaw) * turn, -Math.abs(turn) * 0.3, Math.sin(yaw) * turn]));
    }
    p = v3.add(p, v3.scale(dir, len / steps));
  }
  const green: V3 = shape.kind === 'ghost' ? [0.16, 0.18, 0.07] : [0.1, 0.15, 0.035];
  const dry: V3 = [0.3, 0.23, 0.12];
  b.tube(spine, radius, (k, j) => {
    const t = k / steps, ridge = j % 2 ? 0.8 : 1;
    return v3.scale(v3.lerp(green, dry, smoothstep(0.25, 1, t) * 0.8 + 0.15 * r()), ridge);
  }, MAT.stem, 15, a => 1 + 0.12 * Math.cos(5 * a) + 0.03 * Math.cos(10 * a + 1));
  return b.data();
}

/**
 * ray against the pumpkin's triangles in pumpkin space: the nearest hit, its (u, s), the point on the skin, its normal,
 * and its sheet point `q`. With `bySheet` the ray is cast against the sheet points instead (the neck unbent), and `p`
 * is where that lands on the real skin
 */
export function raycast(g: PumpkinGeometry, o: V3, d: V3, bySheet = false): { t: number; u: number; s: number; p: V3; q: V3; n: V3 } | null {
  const V = g.verts, I = g.index, at = bySheet ? 10 : 0;
  let best: { t: number; u: number; s: number; p: V3; q: V3; n: V3 } | null = null;
  // a bounding sphere first
  const R = Math.max(g.radius * 2.2, Math.abs(g.minY) * 1.2, v3.len(g.top) * 1.2);
  const b = v3.dot(o, d), c = v3.dot(o, o) - R * R;
  if (!bySheet && b * b - c < 0) return null;
  for (let t = 0; t < I.length; t += 3) {
    const ia = I[t] * STRIDE, ib = I[t + 1] * STRIDE, ic = I[t + 2] * STRIDE, a = ia + at;
    const e1: V3 = [V[ib + at] - V[a], V[ib + at + 1] - V[a + 1], V[ib + at + 2] - V[a + 2]];
    const e2: V3 = [V[ic + at] - V[a], V[ic + at + 1] - V[a + 1], V[ic + at + 2] - V[a + 2]];
    const pv = v3.cross(d, e2), det = v3.dot(e1, pv);
    if (Math.abs(det) < 1e-12) continue;
    const inv = 1 / det, tv: V3 = [o[0] - V[a], o[1] - V[a + 1], o[2] - V[a + 2]];
    const u = v3.dot(tv, pv) * inv; if (u < 0 || u > 1) continue;
    const qv = v3.cross(tv, e1), w = v3.dot(d, qv) * inv; if (w < 0 || u + w > 1) continue;
    const tt = v3.dot(e2, qv) * inv;
    if (tt <= 1e-5 || (best && tt >= best.t)) continue;
    const l = 1 - u - w;
    const lerp = (off: number) => V[ia + off] * l + V[ib + off] * u + V[ic + off] * w;
    best = { t: tt, u: lerp(6), s: lerp(7), p: [lerp(0), lerp(1), lerp(2)], q: [lerp(10), lerp(11), lerp(12)], n: v3.norm([lerp(3), lerp(4), lerp(5)]) };
  }
  return best;
}

export type SculptMode = 'pull' | 'push' | 'smooth' | 'grab' | 'poke' | 'inflate';

/**
 * a brush on the sculpt grid at (u, s): radius in radians of the pumpkin, strength per dab; grab moves by `amount`
 * (signed share of the radius) with a soft falloff
 */
export function brush(g: Float32Array, u: number, s: number, mode: SculptMode, radius: number, strength: number, amount = 0) {
  const cx = u * SCULPT_W, cy = s * (SCULPT_H - 1);
  const ry = Math.ceil(radius / Math.PI * (SCULPT_H - 1)) + 1;
  const ring = Math.max(0.05, Math.sin(Math.PI * s));
  const rx = Math.min(SCULPT_W / 2, Math.ceil(radius / (Math.PI * 2 * ring) * SCULPT_W) + 1);
  const before = mode === 'smooth' ? g.slice() : null;
  for (let y = Math.max(0, Math.floor(cy - ry)); y <= Math.min(SCULPT_H - 1, Math.ceil(cy + ry)); y++) {
    for (let x = Math.floor(cx - rx); x <= Math.ceil(cx + rx); x++) {
      const X = ((x % SCULPT_W) + SCULPT_W) % SCULPT_W, i = y * SCULPT_W + X;
      const ds = (y - cy) / (SCULPT_H - 1) * Math.PI;
      const dp = (x + 0.5 - cx) / SCULPT_W * Math.PI * 2 * Math.max(0.05, Math.sin(Math.PI * y / (SCULPT_H - 1)));
      const d = Math.hypot(ds, dp) / radius;
      if (d >= 1) continue;
      const fall = mode === 'poke' ? Math.pow(1 - d, 3) : mode === 'inflate' ? 1 - d * d : (1 - d * d) * (1 - d * d);
      switch (mode) {
        case 'pull': case 'inflate': g[i] += strength * 0.02 * fall; break;
        case 'push': g[i] -= strength * 0.02 * fall; break;
        case 'poke': g[i] -= strength * 0.05 * fall; break;
        case 'grab': g[i] += amount * fall; break;
        case 'smooth': {
          const b = before!, L = b[y * SCULPT_W + (X + SCULPT_W - 1) % SCULPT_W], R = b[y * SCULPT_W + (X + 1) % SCULPT_W];
          const U = b[Math.max(0, y - 1) * SCULPT_W + X], D = b[Math.min(SCULPT_H - 1, y + 1) * SCULPT_W + X];
          g[i] += ((L + R + U + D) / 4 - b[i]) * Math.min(1, strength * fall);
          break;
        }
      }
      g[i] = clamp(g[i], -0.3, 0.3);
    }
  }
}

/** where on the stencil's sheet a direction from the centre falls: matches SHEET in the shaders */
export const SHEET_PHI = 1.25, SHEET_THETA = 1.0;
export function sheetOf(q: V3, squash: number): [number, number] {
  const az = Math.atan2(q[0], q[2]);
  const el = Math.atan2(q[1] / squash, Math.hypot(q[0], q[2]));
  return [az / SHEET_PHI, el / SHEET_THETA];
}
